import React, {  useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { useParams } from "react-router-dom";
import "../../style/Hostage.css";
import {
  Box,
  Button,
  Container,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import StoryForm from "../Feed/StoryForm";
import UnsupportHostageDrawer from "./UnsupportHostageDrawer";
import { Hostage, Story, User } from "../types";
import NoActivitiesSVG from "../../icons/no-activities.svg";
import { decode } from "../../utils/Utils";
import StoryList from "./StoryList";
import { fetchHostageData, fetchHostageStories } from "./hostageFetchUtils";

const HostageFeed = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [hostage, setHostage] = useState<Hostage | null>(null);
  const [isAddStoryDrawerOpen, setIsAddStoryDrawerOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [openConfirmDrawer, setOpenConfirmDrawer] = useState(false);
  const [stories, setStories] = useState<Story[]>([]);

  const navigate = useNavigate();

  useEffect(() => {
    if (!id) {
      console.log("No id found, redirecting to home");
      navigate("/");
      return;
    }

    // @ts-ignore
    fetchHostageData(id)
      .then(({ hostageData, userData }) => {
        setHostage(hostageData);
        setUser(userData);

        // Fetch stories after setting hostage data
        return fetchHostageStories(hostageData.id);
      })
      .then(storiesData => {
        console.log ('setting stories', storiesData)
        setStories(storiesData);
      })
      .catch(() => {
        console.log(`No data found for id: ${id}, redirecting to home`);
        navigate("/"); // Redirect to home if no data is found
      });
  }, []);

  if (!hostage) {
    return <div>{t("hostageCard.loading")}</div>; // Show a loading message while fetching data
  }

  if (!user) {
    return null;
  }

  function getHostageStories(){
    fetchHostageStories(hostage?.id).then((data) => setStories(data));
  }

  function canAddActivities(user: User, hostage: Hostage) {
    return user.role === "admin" ||
      (user.role === "moderator" && user.moderating?.includes(hostage.id));
  }

  const isJoined = user.supported_hostages.includes(hostage.id);
  const ENVIRONMENT = process.env.NODE_ENV || "development";
  const SERVER_BASE_URL = ENVIRONMENT === "development" ? "http://localhost:3001" : "";

  const handleConfirmUnsupport = async () => {
    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/user/unsupport`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ hostageId: id }),
      });
      const res = await response.json();
      console.log("unSupport request successful:", res);

      ReactGA.event({
        category: "Event",
        action: "unSupport Hostage Clicked",
        label: id,
      });
      setOpenConfirmDrawer(false); // Close the drawer after success
      window.location.reload();
    } catch (err) {
      console.error("Error sending unSupport request:", err);
    }
  };

  const handleUnsupportClick = () => {
    setOpenConfirmDrawer(true); // Open the confirmation dialog
  };



  const handleCreateStory = () => {
    setIsAddStoryDrawerOpen(true);
  };
  const handleDeleteStory = (storyId?: string) => {
    if (storyId) {
      setStories(prevStories => prevStories.filter(story => story.id !== storyId));
    }
  };


  return (
    <Container maxWidth="sm" className="hostage-card-container">

      {canAddActivities(user, hostage) ? <Button onClick={handleCreateStory}> {t("createStory.title")}</Button> : null}
      <StoryForm
        open={isAddStoryDrawerOpen}
        onClose={() => {
          setIsAddStoryDrawerOpen(false);
          getHostageStories();
        }}
        hostageId={hostage.id}
      />
      {stories.length === 0 ? (
        <>
          <Typography variant="body1" sx={{ textAlign: "center", fontWeight: 600 }}>
            {decode(t("userFeed.emptyTitle", { name: hostage[i18n.language as "en" | "he"].first_name }))}
          </Typography>
          <Typography variant="body2" sx={{ textAlign: "center", margin: "8px 30px 30px 30px" }}>
            {decode(t("userFeed.emptyHostageFeedSubtitle", { name: hostage[i18n.language as "en" | "he"].first_name }))}
          </Typography>
        </>
      ) : (
        <>
          <StoryList stories={stories} hostage={hostage} onDeleteStory={handleDeleteStory} />
        </>
      )}
      {isJoined &&
        <>
          <Button
            variant="outlined"
            className="hostage-unsupport-btn"
            onClick={handleUnsupportClick}>
            {t("hostageCard.unsupportBtn")}
          </Button>

          {/* Confirmation Dialog */}
          <UnsupportHostageDrawer
            open={openConfirmDrawer}
            onClose={() => setOpenConfirmDrawer(false)}
            onConfirm={handleConfirmUnsupport}
            hostageName={hostage[i18n.language as "en" | "he"].name_with_title}
          />
        </>
      }
    </Container>
  );
};

export default HostageFeed;
